import { Footer } from '@/components/elements/new-footer/Footer'
import { Header } from '@/components/elements/header/Header'
import { CHECKOUT_GB } from '@/lib/checkout/types'
import { useCheckoutCountry } from '@/lib/hooks/useCheckoutCountry'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styles/theme'
import { HowItWorksPageRecord, HowItWorksRecord } from 'types'
import HowItWorksPageContent from './HowItWorksPageContent'
import { SEOMetaTags } from '@/components/shared/SEOMetaTags'

type HowItWorksPageProps = {
  page: HowItWorksPageRecord
}

const StyledMainContainer = styled.main`
  #results {
    padding: 96px 0;
    p {
      font-size: ${theme.v2.typography.body.m.fontSize};
      line-height: ${theme.v2.typography.body.m.lineHeight};
      font-family: ${theme.v2.font.lotaGrotesqueLight};
      color: ${theme.v2.onSurface.subduedText};
      padding: 0;
      margin: 0;
    }
    h2 {
      font-family: ${theme.v2.font.nantes};
      font-size: ${theme.v2.typography.display.xxs.fontSize};
      line-height: ${theme.v2.typography.display.xxs.lineHeight};
      color: ${theme.v2.onSurface.text};
      margin-top: 8px;
      margin-bottom: 16px;
      text-align: center;
    }
    a {
      margin-top: 16px;
    }
    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      padding: 96px 24px;
    }
  }
  .button-group {
    a {
      &:hover {
        background-color: white;
        opacity: 0.9;
      }
    }
  }
  #faqs {
    padding: 96px 0;
    h2 {
      font-family: ${theme.v2.font.nantes};
      font-size: ${theme.v2.typography.display.xxs.fontSize};
      line-height: ${theme.v2.typography.display.xxs.lineHeight};
      padding: 0;
      margin: 0;
    }
    a {
      margin-top: 32px;
    }
    p {
      padding: 0;
      margin: 0;
    }
  }
  #backed-by-science {
    padding: 96px 0;
    p {
      font-size: ${theme.v2.typography.body.xs.fontSize};
      line-height: ${theme.v2.typography.body.xs.lineHeight};
      font-family: ${theme.v2.font.lotaGrotesqueLight};
      color: ${theme.v2.onSurface.subduedText};
      padding: 0;
      margin: 0;
    }
    h2 {
      font-family: ${theme.v2.font.nantes};
      font-size: ${theme.v2.typography.body.xl.fontSize};
      line-height: ${theme.v2.typography.body.xl.lineHeight};
      color: ${theme.v2.onSurface.text};
      margin-top: 8px;
      margin-bottom: 72px;
      padding: 0;
      text-align: center;
    }
    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      padding: 96px 0 48px 0;
    }
  }
  #cta-button {
    margin-top: 0;
    padding-top: 0;
    a {
      margin-top: 0;
    }
  }
`

export const HowItWorksLandingPage: React.FC<HowItWorksPageProps> = ({ page }) => {
  const router = useRouter()
  const { content, metaData } = page
  const { isReady, checkoutCountry } = useCheckoutCountry()
  const [validLocales, setValidLocales] = useState<string[]>(['All', CHECKOUT_GB])

  useEffect(() => {
    if (isReady && checkoutCountry) {
      setValidLocales(['All', checkoutCountry])
    }
  }, [isReady, checkoutCountry])

  const renderedContent = content.filter((record: HowItWorksRecord) => {
    if ('localisation' in record) {
      const localisation = record.localisation[0]
      return validLocales.includes(localisation.country)
    }
    return true
  })
  return (
    <>
      <SEOMetaTags metaData={metaData} path={`/how-it-works`} />
      <Header backgroundColor={theme.v2.surface.white} />
      <StyledMainContainer>
        {renderedContent.map((block, key) => (
          <HowItWorksPageContent key={key} block={block} currentPath={router.asPath} />
        ))}
      </StyledMainContainer>

      <Footer showPreFooter />
    </>
  )
}
